<template>
  <div>
    <v-dialog
      v-if="!error && data"
      v-model="showPreviewDialog"
      max-width="75%"
    >
      <v-card elevation="24">
        <v-row class="no-gutters" style="position: relative;">
          <v-img 
            v-if="thumbnail" v-bind:src="'data:image/jpeg;base64,' + thumbnail" 
            style="box-shadow: 1px 1px 3px 1px rgba(0,0,0,0.1);" 
          >
            <v-row v-if="audio_levels" class="ma-3">
              <audio-level-meter
                v-for="channel in audio_levels" v-bind:key="channel.channel_num"
                :channel="channel.channel_num"
                :db="getAudioLevel(channel.channel_num)"
                :db_5s="getAudioLevel_5s(channel.channel_num)"
                :hideSilentChannels="hide_silent"
                :transparent="true"
              />
            </v-row>
            <div v-if="thumbnail && !hasFocus" style="position: absolute; text-align: center; text-shadow: 1px 1px 2px black; top:5px; right: 5px;" class="caption white--text">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    color="white"
                    dark
                    size="72"
                    v-bind="attrs"
                    v-on="on"
                  >
                    mdi-pause
                  </v-icon>
                </template>
                <span>Preview may be paused if the browser window does not have focus.</span>
              </v-tooltip>
            </div>
          </v-img>
          
          <div style="position: absolute; width: 100%; text-shadow: 1px 1px 2px black; bottom: 10px; text-align: center;" class="caption white--text">
            This is a scaled preview image, not representative of stream output.
          </div>
        </v-row>
      </v-card>
    </v-dialog>
    
    <v-row 
      v-if="error"
      style="position: relative;"
      class="no-gutters my-1"
    >
      <div class="px-3 pt-5 red lighten-5" style="box-shadow: 1px 1px 3px 1px rgba(0,0,0,0.1); text-align: center; width: 100%;">
        <v-icon size="72" color="red" class="mb-3">tv_off</v-icon>
        <p class="text--disabled">{{ errorText }}</p>
      </div>
    </v-row>
    
    <v-row 
      v-else-if="!data"
      style="position: relative;"
      class="no-gutters my-1"
    >
      <v-progress-linear
        class="ma-5"
        color="primary"
        indeterminate
        buffer-value="0"
      ></v-progress-linear>
    </v-row>
    
    <v-row 
      v-else
      class="my-1"
      no-gutters 
    >
      <v-col cols="12">
        <v-img 
          v-if="thumbnail" v-bind:src="'data:image/jpeg;base64,' + thumbnail" 
          style="box-shadow: 1px 1px 3px 1px rgba(0,0,0,0.1); cursor: pointer;" 
          @click="showPreviewDialog = true" 
        >
          <v-row v-if="tile && audio_levels" class="ma-3">
            <audio-level-meter
              v-for="channel in audio_levels" v-bind:key="channel.channel_num"
              :channel="channel.channel_num"
              :db="getAudioLevel(channel.channel_num)"
              :db_5s="getAudioLevel_5s(channel.channel_num)"
              :hideSilentChannels="hide_silent"
              :transparent="true"
            />
          </v-row>
          <div v-if="thumbnail && !hasFocus" style="position: absolute; text-align: center; text-shadow: 1px 1px 2px black; top:5px; right: 5px;" class="caption white--text">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  color="white"
                  dark
                  size="32"
                  v-bind="attrs"
                  v-on="on"
                >
                  mdi-pause
                </v-icon>
              </template>
              <span>Preview may be paused if the browser window does not have focus.</span>
            </v-tooltip>
          </div>
        </v-img>
        
        <div v-else class="px-5 pt-5 red lighten-5" style="box-shadow: 1px 1px 3px 1px rgba(0,0,0,0.1); text-align: center;">
          <v-icon size="72" color="red" class="mb-3">tv_off</v-icon>
          <p class="text--disabled">Thumbnail data not received...</p>
        </div>
        
      </v-col>
      <v-col v-if="!tile && audio_levels" cols="12" class="mt-2">
        <h5>Audio Levels</h5>
        <v-row class="mt-1" no-gutters>
          <audio-level-meter
            v-for="channel in audio_levels" v-bind:key="channel.channel_num"
            :channel="channel.channel_num"
            :db="getAudioLevel(channel.channel_num)"
            :db_5s="getAudioLevel_5s(channel.channel_num)"
          />
        </v-row>
      </v-col>
    </v-row>
  </div>
</template>

<script>
  export default {
    name: 'InputPreview',
    
    props: ['device_guid', 'input_id', 'input_type', 'tile', 'hide_silent'],
    
    data() {
      return {
        inputPreviewSupportedVersion: this.$helpers.parseVersion('cloud', 'v1.3.0'),
        
        error: false,
        errorText: '',
        
        subscription: false,
        
        data: false,
        
        audio_levels: false,
        thumbnail: false,
        
        requestInterval: 15000,
        requestTimer: false,
        
        hasFocus: true,
        showPreviewDialog: false,
        
        destroyed: false,
      }
    },
    
    computed: {
      previewTopic() {
        return 'dt/videon_cloud/device/' + this.device_guid + '/input_preview'
      },
      pubsub() {
        return this.$Amplify.PubSub
      },
      device() {
        return this.$devices.getDevice(this.device_guid)
      },
      isPreviewSupported() {
        // unable to support GStreamer inputs yet...
        if (this.input_type == 'hardware' && this.$helpers.versionCheck(this.inputPreviewSupportedVersion, this.device.cloud_version)) {
          return true
        }
        return false
      },
    },
    
    mounted() {
      window.addEventListener('focus', this.windowFocused)
      
      setTimeout(() => {
        this.setupPreview()
      }, this.$helpers.getRefreshInterval(250, 1000))
    },
    
    beforeDestroy() {
      this.destroyed = true
      window.removeEventListener('focus', this.windowFocused)
      clearTimeout(this.requestTimer)
      
      if (this.subscription) {
        console.log('InputPreview ' + this.device_guid + ' unsubscribe ' + this.previewTopic)
        this.subscription.unsubscribe()
      }
    },
    
    methods: {
      windowFocused() {
        if (this.subscription && this.requestTimer) {
          clearTimeout(this.requestTimer)
          this.requestPreview()
        }
      },
      
      setupPreview() {
        // handle all the error logic here...
        if (!this.device.isOnline()) {
          this.error = true
          this.errorText = 'Input preview unavailable when the device is offline.'
          
          setTimeout(() => {
            this.setupPreview()
          }, 5000)
        } else if (!this.isPreviewSupported) {
          this.error = true
          this.errorText = 'Input preview is not supported.'
        } else if (!this.device.hasFeature('daemon.encoders.video.input_preview.enabled')) {
          this.error = true
          this.errorText = 'Input preview is not licensed.'
          
          setTimeout(() => {
            this.setupPreview()
          }, 5000)
        } else {
          this.subscribeToTopic()
          
          setTimeout(() => {
            this.requestPreview(true)
          }, 250)
        }
      },
      
      subscribeToTopic() {
        if (!this.device_guid) {
          return
        }
        
        if (!this.subscription) {
          console.log('InputPreview ' + this.device_guid + ' subscribeToTopic ' + this.previewTopic)
          
          this.subscription = this.pubsub.subscribe(this.previewTopic).subscribe({
            next: (data) => {
              if (data.value) {
                this.data = data.value
                
                //console.log('DATA', this.data)
                
                // filter on input_id
                if (!this.input_id || (this.data.input_id == this.input_id)) {
                  if (this.data.audio_levels) {
                    this.audio_levels = this.data.audio_levels
                  }
                  if (this.data.thumbnail) {
                    this.thumbnail = this.data.thumbnail
                  }
                }
                
                // reset error
                this.error = false
                this.errorText = ''
              }
            },
            error: (error) => {
              console.log('InputPreview ' + this.device_guid + ' subscribeToTopic Error:', error)
              this.error = true
              this.errorText = this.$helpers.parseError(error)
            },
            complete: () => {
              console.log('InputPreview ' + this.device_guid + ' subscribeToTopic complete')
            }
          })
        }
      },
      
      async requestPreview(firstRun = false) {
        if (this.destroyed) {
          return
        }
        
        this.hasFocus = document.hasFocus()
        
        if (!firstRun && !this.data && document.hasFocus()) {
          this.error = true
          this.errorText = 'Preview data not received within ' + (this.requestInterval / 1000) + ' seconds.  Please check thumbnail output settings and verify they are compatible with input preview.'
        }
        
        if (firstRun || this.hasFocus) {
          let requestSuccess = await this.device.requestInputPreview(firstRun)
        
          if (!requestSuccess) {
            this.error = true
            this.errorText = 'Input preview request failed...'
          }
        }
        
        this.requestTimer = setTimeout(() => {
          if (!this.destroyed) {
            this.requestPreview()
          }
        }, this.requestInterval)
      },
      
      getAudioLevel(channelNumber) {
        var channel = this.audio_levels.find(x => x.channel_num === channelNumber)
        if (channel && channel.max_hold_db_200ms !== undefined) {
          return channel.max_hold_db_200ms
        } else if (channel && channel.max_hold_200ms) {
          return Math.round(20 * Math.log10(channel.max_hold_200ms / 32767))
        }
        return -48
      },
      getAudioLevel_5s(channelNumber) {
        var channel = this.audio_levels.find(x => x.channel_num === channelNumber)
        if (channel && channel.max_hold_db_5s !== undefined) {
          return channel.max_hold_db_5s
        } else if (channel && channel.max_hold_5s) {
          return Math.round(20 * Math.log10(channel.max_hold_5s / 32767))
        }
        return -48
      }
      
    }
  }
</script>